import React, { useState } from "react";
import { useInView } from "../../hooks/useInView";
import AccordionItem from "./AccordionItem";

export type InfoCardData = {
  image: string;
  title: string;
  description: string;
  characteristics?: string[];
  applications?: string;
  direction?: "imageLeft" | "imageRight";
  anchorId?: string;
};

interface InfoCardProps {
  data: InfoCardData[];
}

const GlassInfoCardItem: React.FC<{ item: InfoCardData }> = ({ item }) => {
  const isImageLeft = item.direction !== "imageRight";
  const [ref, inView] = useInView<HTMLDivElement>({ threshold: 0.2 });
  // Use anchorId if provided, otherwise generate from title
  const anchorId = item.anchorId || item.title
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

  return (
    <div
      ref={ref}
      id={anchorId}
      className={`grid grid-cols-1 md:grid-cols-2 gap-[32px] items-start mb-16 pt-[120px] pb-8 ${!isImageLeft ? "md:flex-row-reverse" : ""}`}
    >
      {/* Image */}
      <div className={isImageLeft ? "order-1" : "order-2"}>
        <img
          src={item.image}
          alt={item.title}
          className="w-[624px] h-[541px] object-cover "
        />
      </div>
      {/* Content */}
      <div className={`text-[#0A1E2D] ${isImageLeft ? "order-2" : "order-1"}`}>
        <h2 className="font-utility font-medium text-[54px] leading-[120%] tracking-[-0.01em] align-middle capitalize text-[#011621] mb-2">
          {item.title}
        </h2>
        <hr
          className={`mb-8 mt-8 transition-all duration-1000 delay-150 ease-out origin-left border-[#4F7286] border-t-2 ${inView ? "w-full" : "w-0"}`}
          style={{ borderTop: "2px solid #B3C3CB" }}
        />
        <div className="mb-7">
          <h4 className="font-inter font-normal text-[20px] leading-[150%] tracking-[-0.005em] text-[#4F7286] mb-1">
            Description:
          </h4>
          <p className="font-inter font-normal text-[20px] leading-[150%] tracking-[-0.005em] text-[#033652]">
            {item.description}
          </p>
        </div>
        {item.characteristics && (
          <div className="mb-7">
            <h4 className="font-inter font-normal text-[20px] leading-[150%] tracking-[-0.005em] text-[#4F7286] mb-1">
              Characteristics:
            </h4>
            <ul className="font-inter font-normal text-[20px] leading-[150%] tracking-[-0.005em] text-[#033652] list-disc list-inside space-y-1">
              {item.characteristics.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </div>
        )}
        {item.applications && (
          <div>
            <h4 className="font-inter font-normal text-[20px] leading-[150%] tracking-[-0.005em] text-[#4F7286] mb-1">
             Perfect for:
            </h4>
            <p className="font-inter font-normal text-[20px] leading-[150%] tracking-[-0.005em] text-[#033652]">
              {item.applications}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const InfoCard: React.FC<InfoCardProps> = ({ data }) => {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 600);
const [openIndex, setOpenIndex] = useState<number | null>(0); // default first open

  const handleToggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };
  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 599px)');
    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);

    // Set initial value
    setIsMobile(mediaQuery.matches);

    // Listen for changes
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  if (isMobile) {
    return (
    <section className="bg-[#F5F7FA] px-4 sm:px-6 md:px-20 py-6 md:py-10">
      {data.map((item, idx) => (
        <AccordionItem
          key={idx}
          item={item}
          isOpen={openIndex === idx}
          onClick={() => handleToggle(idx)}
        />
      ))}
    </section>
  );
  }

  return (
    <section className="bg-[#F5F7FA] px-6 md:px-20 py-16">
      {data.map((item, idx) => (
        <GlassInfoCardItem key={idx} item={item} />
      ))}
    </section>
  );
};

export default InfoCard;
